/**
 * The two floating menus: a toolbar over the selection, and the slash palette.
 *
 * Both read the textarea directly rather than taking state from the page. The
 * page deliberately does not re-render while someone is typing — that is the
 * whole point of the editor's design — so anything that has to track the caret
 * has to listen for itself. These listeners are attached to one element and do
 * nothing but compare a few offsets, which is cheap enough to sit on the
 * keystroke path.
 *
 * Positions come from `caretRect`, which measures the highlight backdrop. The
 * backdrop already contains the same text laid out with the same metrics as the
 * textarea, so it doubles as the mirror element this would otherwise have to
 * build and maintain — and it can never drift out of sync with what the author
 * sees, because it *is* what they see.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Code,
  Heading1,
  Heading2,
  Italic,
  Bold,
  Link as LinkIcon,
  List,
  ListOrdered,
  Pilcrow,
  Quote,
  Strikethrough,
  Superscript,
  Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MarkdownEditorHandle } from "./MarkdownEditor";
import {
  insertFootnote,
  insertLink,
  makeSlide,
  setHeading,
  toggleBlockquote,
  toggleBulletList,
  toggleCodeBlock,
  toggleInline,
  toggleOrderedList,
} from "@/lib/markdown-commands";

interface EditorMenusProps {
  editor: MarkdownEditorHandle | null;
}

type SlashState = { query: string; from: number; to: number; left: number; top: number };
type ToolbarState = { left: number; top: number };

const PALETTE_WIDTH = 288;
/** Roughly `commands.length` rows plus padding — keep it in step when adding one. */
const PALETTE_HEIGHT = 372;

const commands = [
  { label: "Text", hint: "Plain paragraph", icon: Pilcrow, terms: "paragraph text" },
  { label: "Heading 1", hint: "Large section heading", icon: Heading1, terms: "h1 title" },
  { label: "Heading 2", hint: "Medium section heading", icon: Heading2, terms: "h2 subtitle" },
  { label: "Bullet list", hint: "Unordered list", icon: List, terms: "ul bullets" },
  { label: "Numbered list", hint: "Ordered list", icon: ListOrdered, terms: "ol numbers" },
  { label: "Quote", hint: "Block quotation", icon: Quote, terms: "blockquote" },
  { label: "Code block", hint: "Preformatted text", icon: Code, terms: "code pre" },
  {
    label: "Footnote",
    hint: "Cite a source",
    icon: Superscript,
    terms: "footnote citation reference source note",
  },
  {
    label: "Slide",
    hint: "Also show this on screen",
    icon: Presentation,
    terms: "slide deck present projector screen powerpoint",
  },
] as const;

type CommandLabel = (typeof commands)[number]["label"];

export function EditorMenus({ editor }: EditorMenusProps) {
  const [slash, setSlash] = useState<SlashState | null>(null);
  const [toolbar, setToolbar] = useState<ToolbarState | null>(null);
  const [selected, setSelected] = useState(0);

  const filtered = useMemo(() => {
    if (!slash) return commands;
    const query = slash.query.toLowerCase();
    return commands.filter((item) =>
      `${item.label} ${item.terms}`.toLowerCase().includes(query),
    );
  }, [slash]);

  /** Recompute both menus from the live textarea. */
  const sync = useCallback(() => {
    const element = editor?.element();
    if (!editor || !element || element.ownerDocument.activeElement !== element) {
      setSlash(null);
      setToolbar(null);
      return;
    }

    const { selectionStart: start, selectionEnd: end } = element;

    if (start !== end) {
      setSlash(null);
      const from = editor.caretRect(start);
      const to = editor.caretRect(end);
      if (!from) {
        setToolbar(null);
        return;
      }
      // Centre over the selection when it sits on one line; anchor to its start
      // when it spans several, where a midpoint would point at nothing.
      const sameLine = to != null && Math.abs(to.top - from.top) < 2;
      const centre = sameLine ? (from.left + to!.right) / 2 : from.left;
      setToolbar({
        left: Math.max(8, Math.min(centre, window.innerWidth - 8)),
        top: Math.max(8, from.top - 10),
      });
      return;
    }

    setToolbar(null);

    const text = element.value;
    const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    const match = /^\/([^\n]*)$/.exec(text.slice(lineStart, start));
    if (!match) {
      setSlash(null);
      return;
    }

    const rect = editor.caretRect(start);
    if (!rect) {
      setSlash(null);
      return;
    }

    setSlash({
      query: match[1].trim(),
      from: lineStart,
      to: start,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - PALETTE_WIDTH - 8)),
      top: Math.min(rect.bottom + 8, window.innerHeight - PALETTE_HEIGHT),
    });
  }, [editor]);

  useEffect(() => {
    const element = editor?.element();
    if (!element) return;

    const events = ["input", "select", "keyup", "mouseup", "focus", "blur"] as const;
    for (const name of events) element.addEventListener(name, sync);
    // Both menus are positioned in viewport coordinates, so scrolling moves the
    // text out from under them.
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);

    return () => {
      for (const name of events) element.removeEventListener(name, sync);
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [editor, sync]);

  useEffect(() => setSelected(0), [slash?.query]);

  const runSlash = useCallback(
    (label: CommandLabel) => {
      const element = editor?.element();
      if (!editor || !element || !slash) return;

      // Drop the `/query` first, then act on what's left. Reading the element
      // back rather than predicting the result keeps the two steps honest —
      // `applyEdit` writes through the browser, so the DOM is the source of
      // truth for what the document now says.
      editor.runEdit({
        start: slash.from,
        end: slash.to,
        text: "",
        caret: { start: slash.from, end: slash.from },
      });

      const text = element.value;
      const at = { start: slash.from, end: slash.from };

      switch (label) {
        case "Text":
          editor.runEdit(setHeading(text, at, 0));
          break;
        case "Heading 1":
          editor.runEdit(setHeading(text, at, 1));
          break;
        case "Heading 2":
          editor.runEdit(setHeading(text, at, 2));
          break;
        case "Bullet list":
          editor.runEdit(toggleBulletList(text, at));
          break;
        case "Numbered list":
          editor.runEdit(toggleOrderedList(text, at));
          break;
        case "Quote":
          editor.runEdit(toggleBlockquote(text, at));
          break;
        case "Code block":
          editor.runEdit(toggleCodeBlock(text, at));
          break;
        case "Footnote":
          editor.runEdit(insertFootnote(text, at));
          break;
        case "Slide":
          editor.runEdit(makeSlide(text, at));
          break;
      }

      setSlash(null);
      editor.focus();
    },
    [editor, slash],
  );

  useEffect(() => {
    if (!slash) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setSlash(null);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelected((value) => (value + 1) % Math.max(filtered.length, 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelected(
          (value) => (value - 1 + Math.max(filtered.length, 1)) % Math.max(filtered.length, 1),
        );
      } else if (event.key === "Enter" && filtered[selected]) {
        event.preventDefault();
        event.stopPropagation();
        runSlash(filtered[selected].label);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [filtered, runSlash, selected, slash]);

  const inline = (marker: string) => () => {
    if (!editor) return;
    const element = editor.element();
    if (!element) return;
    editor.runEdit(toggleInline(element.value, editor.getSelection(), marker));
  };

  const link = () => {
    const element = editor?.element();
    if (!editor || !element) return;
    const url = window.prompt("Link URL", "https://");
    if (url) editor.runEdit(insertLink(element.value, editor.getSelection(), url));
  };

  const quote = () => {
    const element = editor?.element();
    if (!editor || !element) return;
    editor.runEdit(toggleBlockquote(element.value, editor.getSelection()));
  };

  /**
   * Make the selection a slide.
   *
   * Sits in the selection toolbar rather than only in the slash palette because
   * the gesture is usually retrospective: you write the sermon, then read it
   * back and decide which lines belong on the screen. `makeSlide` decides from
   * what is selected: a heading gets a marker, a passage gets wrapped and stays
   * in the manuscript, and an empty line opens a block to type into.
   */
  const slide = () => {
    const element = editor?.element();
    if (!editor || !element) return;
    editor.runEdit(makeSlide(element.value, editor.getSelection()));
  };

  return (
    <>
      {toolbar && (
        <div
          className="fixed z-[60] -translate-x-1/2 -translate-y-full flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg"
          style={{ left: toolbar.left, top: toolbar.top }}
          // Keeping focus in the textarea is what lets every command go through
          // the browser's own editing path, and so onto its undo stack.
          onMouseDown={(event) => event.preventDefault()}
        >
          {[
            { key: "bold", label: "Bold", icon: Bold, action: inline("**") },
            { key: "italic", label: "Italic", icon: Italic, action: inline("*") },
            { key: "strike", label: "Strikethrough", icon: Strikethrough, action: inline("~~") },
            { key: "code", label: "Code", icon: Code, action: inline("`") },
          ].map((item) => (
            <Button
              key={item.key}
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title={item.label}
              onClick={item.action}
            >
              <item.icon className="h-4 w-4" />
            </Button>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Link"
            onClick={link}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Quote"
            onClick={quote}
          >
            <Quote className="h-4 w-4" />
          </Button>
          <span className="mx-0.5 h-5 w-px bg-border" aria-hidden="true" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Make a slide"
            onClick={slide}
          >
            <Presentation className="h-4 w-4" />
          </Button>
        </div>
      )}

      {slash && (
        <div
          className="fixed z-[60] w-72 overflow-hidden rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl"
          style={{ left: slash.left, top: slash.top }}
        >
          <p className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Insert block
          </p>
          {filtered.length ? (
            filtered.map((item, index) => (
              <button
                key={item.label}
                type="button"
                className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left ${
                  index === selected ? "bg-muted" : "hover:bg-muted/70"
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => runSlash(item.label)}
                onMouseEnter={() => setSelected(index)}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-md border bg-background">
                  <item.icon className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-medium">{item.label}</span>
                  <span className="block text-xs text-muted-foreground">{item.hint}</span>
                </span>
              </button>
            ))
          ) : (
            <p className="px-3 py-5 text-center text-sm text-muted-foreground">
              No matching blocks
            </p>
          )}
        </div>
      )}
    </>
  );
}
